import { usePostHog } from 'posthog-js/react';
import { type ReactNode, useEffect } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { EVENTS } from '@apps/web/lib/events';

export type NextStepButtonsProps = Readonly<{
    readonly actions: ReadonlyArray<
        Readonly<{ readonly label: string; readonly prompt: string }>
    >;
    readonly onSelect: (prompt: string) => void;
}>;

export function NextStepButtons({
    actions,
    onSelect,
}: NextStepButtonsProps): ReactNode {
    const posthog = usePostHog();
    const shownCount = actions.length;
    useEffect(() => {
        posthog?.capture(EVENTS.V3_NEXT_STEPS_SHOWN, { count: shownCount });
    }, [posthog, shownCount]);
    return (
        <div
            aria-label="Suggested next steps"
            className="mt-2 flex min-w-0 flex-wrap gap-1.5"
            data-testid="next-step-buttons"
            role="group"
        >
            {actions.map((action, index) => (
                <Button
                    className="text-v3-chat h-8 rounded-md px-2 font-semibold"
                    data-testid="next-step-button"
                    key={action.label}
                    onClick={() => {
                        posthog?.capture(EVENTS.V3_NEXT_STEP_CLICKED, {
                            index,
                            label: action.label,
                        });
                        onSelect(action.prompt);
                    }}
                    size="sm"
                    title={action.prompt}
                    type="button"
                    variant="outline"
                >
                    <ResolvedReferenceText text={action.label} />
                </Button>
            ))}
        </div>
    );
}
